<script context="module" lang="ts">
  import { getApiPaths } from '$lib/stores/getUserStore';
  import type { Load } from '@sveltejs/kit';

  import { API } from 'shared/src/utils/api/api';

  interface SectionInfo {
    sectionName: string;
    teachersString: string;
    institution: string;
  }

  export const load: Load = async ({ params, fetch }) => {
    const api = new API(getApiPaths(), fetch);

    const inviteLink = params.inviteLink;
    if (!inviteLink) {
      return {
        status: 400,
        error: 'An invite link must be provided to join a section',
      };
    }

    try {
      const sectionInfo = await api.$get<SectionInfo>(`/section/class-info/${inviteLink}`);
      return {
        props: {
          sectionInfo,
          isInvalid: false,
          inviteLink,
        },
      };
    } catch (err: any) {
      if (err?.status === 404) {
        return {
          props: {
            isInvalid: true,
            inviteLink,
          },
        };
      }
      console.error(err);
      return {
        status: err?.status || 500,
        error: err?.message || 'Unable to join that section.',
      };
    }
  };
</script>

<script lang="ts">
  import H from '$lib/components/H.svelte';
  import PageTitle from '$lib/teach/resources/PageTitle.svelte';
  import LoginRegisterTabs from '$lib/app/authentication/LoginRegisterTabs.svelte';
  import { goto } from '$app/navigation';
    import { openModal } from '$lib/stores/modals.store';
  export let sectionInfo: SectionInfo | undefined = undefined;
  export let inviteLink: string;
  export let isInvalid: boolean;
</script>

<div class="article">
  <PageTitle title="Join a uTheory Class" doNotAutoCapitalize description="Join a uTheory class" />
  {#if sectionInfo && !isInvalid}
    <H level={2}>You've been invited to join {sectionInfo.sectionName}</H>
    <p>
      By registering or signing in at this link, you give permission for uTheory to share your work,
      progress, scores and personal information with the teachers of this class ({sectionInfo.teachersString})
      or other teachers at
      {sectionInfo.institution || 'their institution'}.
    </p>
  {:else}
    <H level={2} class="text-red">That link is not valid</H>
    <div class="bg-red-50 p-2 mb-2">
      <p class="text-red-900">
        Sorry, that class invitation link is not valid. Please ask your teacher for a new link.
      </p>
      <p>
        You can continue to register as an independent user, and join your teacher's class later
        using a class invite code from the dashboard.
      </p>
    </div>
  {/if}
</div>

<LoginRegisterTabs
  parameters={{ inviteLink: isInvalid ? undefined : inviteLink }}
  on:loginComplete={() => {
    goto('/app');
  }}
  on:registrationComplete={() => {
    goto('/app');
  }}
  on:forgottenPassword={() => {
    openModal('passwordReset');
  }}
/>
